import { format } from "date-fns";
import { ArrowLeft, CheckCircle } from "lucide-react";
import { forwardRef } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";

interface QuotationItem {
  id: string;
  component_type: string;
  product_name: string;
  product_description?: string;
  quantity: number;
  unit_price: number;
  total_price: number;
  notes?: string;
}

interface QuotationProject {
  id: string;
  customer_name: string;
  email?: string;
  phone?: string;
  address?: string;
  city?: string;
  budget?: string;
  timeframe?: string;
  description?: string;
  services?: string[];
  created_at: string;
}

interface QuotationViewProps {
  project: QuotationProject;
  items: QuotationItem[];
  quoteNumber?: string;
  quoteName?: string;
  quoteNotes?: string;
  onBack?: () => void;
}

export const QuotationView = forwardRef<HTMLDivElement, QuotationViewProps>(
  ({ project, items, quoteNumber, quoteName, quoteNotes, onBack }, ref) => {
    const today = new Date();
    const quotationNumber = quoteNumber || `Q-${project.id.slice(0, 8).toUpperCase()}`;

    // Transform items into numbered line format - NO GRID, NO IMAGES
    const lineItems = items.map((item, index) => ({
      id: index + 1,
      title: `${item.product_name}${item.product_description ? ` - ${item.product_description}` : ""}`,
      quantity: item.quantity,
      unitPrice: item.unit_price,
      totalPrice: item.total_price,
      notes: item.notes,
      componentType: item.component_type,
    }));

    const total = items.reduce((sum, item) => sum + item.total_price, 0);

    return (
      <div ref={ref} className="min-h-screen bg-background">
        {/* Header - similar to BathroomQuote but for quote viewing */}
        <header className="border-b bg-card sticky top-0 z-10">
          <div className="container mx-auto px-4 py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                {onBack && (
                  <Button variant="outline" size="sm" onClick={onBack} className="print:hidden">
                    <ArrowLeft className="w-4 h-4 mr-2" />
                    Back
                  </Button>
                )}
                <div>
                  <h1 className="text-2xl font-bold text-foreground">
                    Quote for {project.customer_name}
                  </h1>
                  <p className="text-sm text-muted-foreground">
                    Quote No: {quotationNumber} | {lineItems.length} items
                  </p>
                  {quoteName && (
                    <p className="text-sm text-muted-foreground">Quote Name: {quoteName}</p>
                  )}
                  <p className="text-sm text-muted-foreground">
                    Date: {format(today, "MMMM dd, yyyy")}
                  </p>
                </div>
              </div>
              <div className="text-right">
                <div className="text-2xl font-bold text-primary">${total.toLocaleString()}</div>
                <div className="text-sm text-muted-foreground">Total</div>
              </div>
            </div>
          </div>
        </header>

        {/* Main Content - Numbered Line Items */}
        <main className="container mx-auto px-4 py-8">
          <div className="space-y-4">
            {lineItems.map((item) => (
              <Card key={item.id} className="border-green-500 bg-green-50">
                <CardHeader className="pb-4">
                  <CardTitle className="flex items-center justify-between text-lg">
                    <div className="flex items-center space-x-3">
                      <span className="flex items-center justify-center w-8 h-8 rounded-full text-sm font-bold bg-green-500 text-white">
                        {item.id}
                      </span>
                      <div>
                        <span className="block">{item.title}</span>
                        {item.notes && (
                          <span className="text-sm text-muted-foreground block mt-1">
                            Note: {item.notes}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center space-x-4">
                      <div className="text-right">
                        <div className="text-sm text-muted-foreground">
                          Qty: {item.quantity} × ${item.unitPrice}
                        </div>
                        <div className="font-semibold">${item.totalPrice.toLocaleString()}</div>
                      </div>
                      <CheckCircle className="w-5 h-5 text-green-500" />
                    </div>
                  </CardTitle>
                </CardHeader>
              </Card>
            ))}
          </div>

          {/* Quote Notes */}
          {quoteNotes && (
            <Card className="mt-6">
              <CardHeader>
                <CardTitle>Quote Notes</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground">{quoteNotes}</p>
              </CardContent>
            </Card>
          )}

          {/* Quote Summary */}
          <Card className="mt-6 border-primary">
            <CardHeader>
              <CardTitle>Quote Summary</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                <div className="flex justify-between">
                  <span>Subtotal:</span>
                  <span>${(total / 1.1).toFixed(2)}</span>
                </div>
                <div className="flex justify-between">
                  <span>GST (10%):</span>
                  <span>${((total * 0.1) / 1.1).toFixed(2)}</span>
                </div>
                <div className="flex justify-between font-bold text-lg border-t pt-2">
                  <span>Total:</span>
                  <span>${total.toLocaleString()}</span>
                </div>
              </div>
            </CardContent>
          </Card>
        </main>
      </div>
    );
  }
);

QuotationView.displayName = "QuotationView";
